import React, { useEffect, useState } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/sections/Footer';
import { Crown, HeartHandshake, Utensils, PenTool, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../lib/api';
import ImageCrossfade from '../components/ui/ImageCrossfade';
import { useText } from '../lib/siteContent';

const ServiceArticle = ({ id, title, icon, description, details, images, index }) => {
    const t = useText();
    return (
        <section id={id} className="min-h-screen w-full flex flex-col md:flex-row bg-[#FFFFFF] overflow-hidden group border-b border-[#111111]/5 snap-start">
            
            {/* Split Layout with Overlap */}
            <div className={`w-full md:w-[45%] relative h-[50vh] md:h-auto ${index % 2 !== 0 ? 'md:order-2' : ''}`}>
                 <ImageCrossfade images={images} className="w-full h-full" alt={title} interval={4500} />
                 {/* Editorial Number */}
                 <div className="absolute top-10 left-10 md:left-auto md:right-10 text-[#FFFFFF] text-8xl font-serif z-10 opacity-50 mix-blend-overlay">
                     0{index + 1}
                 </div>
            </div>

            <div className="w-full md:w-[55%] p-8 md:p-32 flex flex-col justify-center relative z-10">
                <div className="max-w-xl">
                     <motion.div 
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                     >
                        <div className="text-[#111111] mb-8">{icon}</div>
                        <span className="text-xs uppercase tracking-[0.4em] text-[#111111] mb-6 block">{t('services.label')}</span>
                        <h2 className="text-5xl md:text-5xl lg:text-6xl xl:text-7xl font-serif text-[#111111] mb-10 leading-[0.95] break-words">{title}</h2>
                        <div className="w-24 h-[1px] bg-[#111111] mb-10"></div>
                        <p className="text-[#6B6B6B] text-xl leading-relaxed mb-12 font-serif italic font-light">"{description}"</p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-12 mb-16">
                            {details.map((detail, idx) => (
                                <div key={idx} className="flex items-center gap-4 group/item cursor-pointer border-b border-[#111111]/10 pb-4">
                                    <span className="w-1.5 h-1.5 bg-[#111111] rotate-45"></span>
                                    <span className="text-sm uppercase tracking-widest text-[#111111] group-hover/item:translate-x-2 transition-transform duration-300">{detail}</span>
                                </div>
                            ))}
                        </div>

                        <a href="/devis" className="group inline-flex items-center gap-4 text-sm uppercase tracking-[0.3em] text-[#111111] hover:text-[#6B6B6B] transition-colors">
                            {t('services.cta')}
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-2" />
                        </a>
                     </motion.div>
                </div>
            </div>

        </section>
    );
};

const ServicesPage = () => {
    const [photos, setPhotos] = useState({});
    const t = useText();

    useEffect(() => {
        api.get('/site-photos').then(({ data }) => {
            const map = {};
            data.forEach((p) => { map[p.slot] = p.images || []; });
            setPhotos(map);
        }).catch(() => {});
    }, []);

    const fb = (slot, ext) => (photos[slot] && photos[slot].length > 0) ? photos[slot] : [ext];

    const services = [
        {
          id: "design",
          title: t('services.s1.title'),
          icon: <Crown className="w-10 h-10" />,
          description: t('services.s1.desc'),
          details: t('services.s1.details').split(',').map((x) => x.trim()).filter(Boolean),
          images: fb('service-design', "https://images.unsplash.com/photo-1758307390525-46a0825d4e50?q=80&w=1920&auto=format&fit=crop")
        },
        {
          id: "planning",
          title: t('services.s2.title'),
          icon: <HeartHandshake className="w-10 h-10" />,
          description: t('services.s2.desc'),
          details: t('services.s2.details').split(',').map((x) => x.trim()).filter(Boolean),
          images: fb('service-planning', "https://images.unsplash.com/photo-1615999106682-093ac6c98f90?q=80&w=1920&auto=format&fit=crop")
        },
        {
          id: "service",
          title: t('services.s3.title'),
          icon: <Utensils className="w-10 h-10" />,
          description: t('services.s3.desc'),
          details: t('services.s3.details').split(',').map((x) => x.trim()).filter(Boolean),
          images: fb('service-service', "https://images.unsplash.com/photo-1712314947761-a8d718bd8c32?q=80&w=1920&auto=format&fit=crop")
        },
        {
          id: "stationery",
          title: t('services.s4.title'),
          icon: <PenTool className="w-10 h-10" />,
          description: t('services.s4.desc'),
          details: t('services.s4.details').split(',').map((x) => x.trim()).filter(Boolean),
          images: fb('service-stationery', "https://images.unsplash.com/photo-1750208759761-6a32d532e426?q=80&w=1920&auto=format&fit=crop")
        }
    ];

  return (
        <div className="bg-[#FFFFFF] text-[#111111] h-screen overflow-y-scroll snap-y snap-mandatory no-scrollbar">
            <div className="fixed top-0 left-0 w-full z-50">
                <Navbar />
            </div>
            
            {/* Editorial Intro */}
            <section className="min-h-[80vh] flex flex-col justify-center items-center text-center px-6 bg-[#FFFFFF] text-[#111111] relative overflow-hidden pt-32 snap-start">
                <div className="relative z-10 max-w-5xl">
                     <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1 }}
                     >
                        <span className="text-xs uppercase tracking-[0.4em] text-[#111111] mb-10 block">{t('services.kicker')}</span>
                        <h1 className="text-7xl md:text-[10rem] font-serif mb-12 leading-[0.8] tracking-tighter">
                            {t('services.title1')}<br/><span className="italic font-light ml-20 text-[#111111]/50">{t('services.title2')}</span>
                        </h1>
                     </motion.div>
                </div>
            </section>

            {services.map((service, index) => (
                <ServiceArticle 
                    key={service.id} 
                    {...service} 
                    index={index} 
                />
            ))}
            
            <div className="snap-start">
                <Footer />
            </div>
        </div>
  );
};

export default ServicesPage;
